package com.arena_web.ui.sections.auth

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.auth.AppleSignInButton
import com.arena_web.ui.components.auth.GoogleSignInButton
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Figma 10093:238333 — sign-in landing rendered for unauthenticated visitors
 * hitting the Profile or Chat pages. The surrounding chrome (sport switcher,
 * logo, bottom nav, …) is provided by [AppPageLayout]; this composable fills
 * the body.
 *
 * Desktop keeps the original top-anchored layout with side-by-side OAuth pills;
 * phones get the [GuestWelcomeCard] (Figma 10272:225842, minus the Overview
 * header + segmented control) sitting a little above the vertical centre.
 */
@Composable
fun WelcomeToArenaScreen(
    onSignedIn: () -> Unit = {}
) {
    if (isMobile()) {
        WelcomeMobile(onSignedIn)
        return
    }

    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    Box(
        modifier = Modifier
            .fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPaddingTop(220)
                .sGap(32)
                .styleModifier { property("align-items", "center") }
        ) {
            Column(
                modifier = Modifier
                    .sGap(12)
                    .styleModifier { property("align-items", "center") }
            ) {
                SpanText(
                    t.authWelcomeTitle,
                    modifier = Modifier
                        .sFontSize(32)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(38)
                        .sLetterSpacing(0.64)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .textAlign(TextAlign.Center)
                )
                SpanText(
                    t.authWelcomeSubtitle,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .textAlign(TextAlign.Center)
                )
            }

            Row(
                modifier = Modifier.sGap(12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                GoogleSignInButton(onLoggedIn = onSignedIn)
                AppleSignInButton(onLoggedIn = onSignedIn)
            }

            Row(
                modifier = Modifier.sGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "${t.authAgreePrefix} ",
                    modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                SpanText(
                    t.authTerms,
                    modifier = Modifier.bodyMRegular().color(AppColors.Dark.OnBackground).fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo("/terms") }
                )
                SpanText(" & ", modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text2))
                SpanText(
                    t.authPrivacy,
                    modifier = Modifier.bodyMRegular().color(AppColors.Dark.OnBackground).fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo("/privacy") }
                )
            }
        }
    }
}

// Full-body phone variant — the welcome card centred and nudged a little above
// the midline so it doesn't sit too low under the top bar.
@Composable
private fun WelcomeMobile(onSignedIn: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            // Reserve room for the fixed bottom nav (sits at bottom 24 + ~73 px
            // pill) so the card stretches from under the top bar to just above it.
            .mPaddingBottom(108),
        contentAlignment = Alignment.Center
    ) {
        // Fills the full body height; content is vertically centred inside.
        GuestWelcomeCard(
            onSignedIn = onSignedIn,
            modifier = Modifier.fillMaxWidth().fillMaxHeight()
        )
    }
}

/**
 * Figma 10272:225842 (the Overview "Achievements" container) reduced to just
 * the sign-in prompt — the "Overview" header and the segmented control are
 * dropped. The gradient-bordered card holds the "Welcome to the Arena!" copy,
 * stacked full-width Google/Apple pills, and the Terms/Privacy line.
 *
 * Shared by the Profile/Chat full-page guest view ([WelcomeMobile]) and the
 * mobile account-required popup, so the two surfaces stay identical.
 */
@Composable
fun GuestWelcomeCard(
    onSignedIn: () -> Unit,
    modifier: Modifier = Modifier,
    /** null → translucent Overview-card surface (Profile/Chat, on the page bg).
     *  Pass a solid colour (e.g. the BottomSheet's `var(--l-s2, #09142d)`) when the card sits
     *  over a popup scrim and needs an opaque fill. */
    fillColor: String? = null,
) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    Column(
        modifier = modifier
            .mBorderRadius(22)
            .mPaddingLeft(14).mPaddingRight(14)
            .mPaddingTop(36).mPaddingBottom(32)
            .mGap(32)
            .styleModifier {
                // Taller block (echoes the Figma card footprint); content is
                // vertically centred within it.
                property("min-height", m(440))
                property("justify-content", "center")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                // Default: identical to the Profile Overview `ContainerCard`
                // (faint translucent gradient). Popup: solid sheet-coloured fill.
                property(
                    "background",
                    fillColor
                        ?: "linear-gradient(-20deg, rgba(52, 119, 250, 0.018) 27%, " +
                        "rgba(2, 4, 11, 0.3) 140%)"
                )
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier.mGap(12),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                t.authWelcomeTitle,
                modifier = Modifier
                    .mFontSize(20)
                    .fontWeight(FontWeight.Medium)
                    .mLineHeight(26)
                    .mLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .textAlign(TextAlign.Center)
            )
            SpanText(
                t.authWelcomeSubtitle,
                modifier = Modifier
                    .mWidth(308)
                    .mFontSize(14)
                    .fontWeight(FontWeight.Normal)
                    .mLineHeight(22)
                    .mLetterSpacing(0.028)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .textAlign(TextAlign.Center)
            )
        }

        Column(
            modifier = Modifier.fillMaxWidth().mGap(18),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // OAuth pills size with the desktop s() helpers; map `--scale`
            // onto `--mobile-scale` so they render at the 390-px baseline.
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("--scale", "var(--mobile-scale)")
                }
            }) {
                Column(
                    modifier = Modifier.fillMaxWidth().sGap(12)
                ) {
                    GoogleSignInButton(fullWidth = true, onLoggedIn = onSignedIn)
                    AppleSignInButton(fullWidth = true, onLoggedIn = onSignedIn)
                }
            }

            Row(
                modifier = Modifier.mGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.authAgreePrefix,
                    modifier = Modifier.mFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                SpanText(
                    t.authTerms,
                    modifier = Modifier.mFontSize(14).fontWeight(FontWeight.Medium)
                        .color(AppColors.Dark.OnBackground)
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo("/terms") }
                )
                SpanText("&", modifier = Modifier.mFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2))
                SpanText(
                    t.authPrivacy,
                    modifier = Modifier.mFontSize(14).fontWeight(FontWeight.Medium)
                        .color(AppColors.Dark.OnBackground)
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo("/privacy") }
                )
            }
        }
    }
}
